import {createSelector} from '@reduxjs/toolkit'

import {eq, isNull} from '@shared/helpers/lang'

import {PayMethod, MAXIMUM_LIMIT_CHARGE_POINT_FOR_RESTRICTED_USER, SubscribePayMethod} from '@web/core/constants'
import {pageSetInfoSelector} from '@web/slices/common/slice'
import {
    availablePayMethodTypesSelector,
    priorityPaymentMethodSelector,
    selectedEasyBankAccountSelector,
    selectedEasyCardSelector,
    selectedMethodSelector,
    selectedPaymentMobileSelector,
    selectedSecondEasyBankAccountSelector,
    selectedSecondEasyCardSelector,
    selectedSecondWithdrawalAccountSelector,
    selectedWithdrawalAccountSelector,
} from '@web/slices/priorityPaymentMethod/slice'
import {isRestrictedUserSelector} from '@web/slices/purchaser/slice'
import {REGISTER_TYPE, subscribeSelector} from '@web/slices/subscribe/slice'

import {pointAmountWithLimitPolicySelector, pointTogglePaymentSelector} from './chargePoint'
import {getRegisterPaymentGatewayParams} from './utils'
import {
    getSecondPriorityPaymentMethodByDiconPolicy,
    getSecondPriorityPaymentMethodByBookingPolicy,
} from './utils/priorityPaymentMethod'

/** 정기결제 등록 API 요청시 필요한 fields */
export const paymentGatewayFieldsSelector = createSelector(
    priorityPaymentMethodSelector,
    selectedEasyBankAccountSelector,
    selectedEasyCardSelector,
    selectedWithdrawalAccountSelector,
    selectedPaymentMobileSelector,
    (
        {payMethod: {billingPayMethods}, selectedPayment: {billingPayMethod}},
        account,
        card,
        withdrawalAccount,
        {mobileNumbers},
    ) => {
        if (!isNull(billingPayMethod)) {
            const selectedBillingPayMethodValue = billingPayMethods[billingPayMethod]
            const origin = {
                pgcd: selectedBillingPayMethodValue.pgCd,
                pgid: selectedBillingPayMethodValue.pgId,
            }

            return getRegisterPaymentGatewayParams(billingPayMethod, origin, {
                account,
                card,
                withdrawalAccount,
                mobile: {mobileNumbers},
            })
        }

        return null
    },
)

/** 정기결제 등록 API 요청시 필요한 fields */
export const secondaryPaymentGatewayFieldsSelector = createSelector(
    priorityPaymentMethodSelector,
    selectedSecondEasyBankAccountSelector,
    selectedSecondEasyCardSelector,
    selectedSecondWithdrawalAccountSelector,
    selectedPaymentMobileSelector,
    (
        {payMethod: {billingPayMethods}, selectedSecondPayment: {billingPayMethod}},
        account,
        card,
        withdrawalAccount,
        {mobileNumbers},
    ) => {
        if (!isNull(billingPayMethod)) {
            const selectedBillingPayMethodValue = billingPayMethods[billingPayMethod]
            const origin = {
                pgcd: selectedBillingPayMethodValue.pgCd,
                pgid: selectedBillingPayMethodValue.pgId,
            }

            const result = getRegisterPaymentGatewayParams(billingPayMethod, origin, {
                account,
                card,
                withdrawalAccount,
                mobile: {mobileNumbers},
            })

            return result
        }

        return null
    },
)

/**
 * 본인인증 미진행 or 간편계좌 미등록된 경우
 * - 페이머니 50만원 이상 보유 시
 * - 혹은 페이머니 보유 금액 + 충전예상 금액이 50만원 이상일 경우
 * */
export const availableRechargePointPayMethodSelector = createSelector(
    isRestrictedUserSelector,
    pointAmountWithLimitPolicySelector,
    pointTogglePaymentSelector,
    (isRestrictedUser, {chargePointAmount}, {expectedRechargePointAmount}) => {
        return (
            isRestrictedUser &&
            chargePointAmount + expectedRechargePointAmount >= MAXIMUM_LIMIT_CHARGE_POINT_FOR_RESTRICTED_USER
        )
    },
)

/**
 * 각 결제수단 별 이용 가능 여부
 * - 서버에서 이용 가능 여부를 판단하고 결정한 응답에 의해 영향을 받습니다.
 * - 사용자의 선택에 의해서 가능 여부가 바뀔 수 없습니다.
 */
export const payMethodAvailableSelector = createSelector(
    availablePayMethodTypesSelector,
    subscribeSelector,
    (availablePayMethodTypes, {isPayPointExcluded}) => {
        const availablePayMethod = [...availablePayMethodTypes]

        if (isPayPointExcluded && availablePayMethod.includes(PayMethod.RECHARGE_POINT)) {
            return availablePayMethod.filter((payMethod) => payMethod !== PayMethod.RECHARGE_POINT)
        }

        return availablePayMethod
    },
)

/**
 * 1차 결제수단 리스트
 */
export const paymentTabRenderSelector = createSelector(
    pageSetInfoSelector,
    payMethodAvailableSelector,
    ({isDiconAuto, isPaymentMethodManagement}, availablePayMethodMap) => {
        if (isPaymentMethodManagement) {
            return [PayMethod.RECHARGE_POINT, PayMethod.CARD_EASY_PAY]
        } else if (isDiconAuto) {
            return availablePayMethodMap
        }
        return []
    },
)

export const isEasyRegisterSelector = createSelector(
    subscribeSelector,
    priorityPaymentMethodSelector,
    ({registerType}, {isLoadFailedEasyAccounts, isLoadFailedEasyCards, selectedPayment: {selectedMethod}}) => {
        const isEasyRegister = registerType === REGISTER_TYPE.SIMPLE
        const eqSelectedMethod = eq(selectedMethod)

        if (isEasyRegister) {
            if (
                (eqSelectedMethod(SubscribePayMethod.BANK_EASY_PAY) ||
                    eqSelectedMethod(SubscribePayMethod.RECHARGE_POINT)) &&
                !isLoadFailedEasyAccounts
            ) {
                return true
            } else if (eqSelectedMethod(SubscribePayMethod.CARD_EASY_PAY) && !isLoadFailedEasyCards) {
                return true
            }
        }

        return false
    },
)

/**
 * 2차 결제수단 리스트
 */
export const paymentSecondTabRenderSelector = createSelector(
    pageSetInfoSelector,
    payMethodAvailableSelector,
    selectedMethodSelector,
    ({isPaymentMethodManagement}, availablePayMethodMap, selectedPrimaryPayMethod) => {
        if (isPaymentMethodManagement) {
            return getSecondPriorityPaymentMethodByBookingPolicy(selectedPrimaryPayMethod)
        } else {
            return getSecondPriorityPaymentMethodByDiconPolicy(availablePayMethodMap)
        }
    },
)
